import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';

interface AdminLevelMappingsProps {
  sport: string;
}

interface LevelMapping {
  id?: string;
  code: string;
  name?: string;
  isRequired: boolean;
  isOptional: boolean;
  displayOrder: number;
}

interface MasterItem {
  code: string;
  name: string;
  strokeType?: string;
  strokeAction?: string;
  playType?: string;
  shotType?: string;
  courtPosition?: string;
  halfSide?: string;
  placement?: string;
  deception?: string;
}

const skillLevels = [
  { value: 'beginner', label: 'Level 1: Beginner' },
  { value: 'intermediate', label: 'Level 2: Intermediate' },
  { value: 'advanced', label: 'Level 3: Advanced' },
  { value: 'pro', label: 'Level 4: Pro/Expert' },
];

const categories = [
  { value: 'strokes', label: 'Strokes & Shots' },
  { value: 'drills', label: 'Drills' },
  { value: 'fitness', label: 'Fitness' },
];

const AdminLevelMappings: React.FC<AdminLevelMappingsProps> = ({ sport }) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [selectedLevel, setSelectedLevel] = useState<string>('beginner');
  const [selectedCategory, setSelectedCategory] = useState<string>('strokes');
  const [mappings, setMappings] = useState<LevelMapping[]>([]);
  const [masterItems, setMasterItems] = useState<MasterItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
  const [playerViewOpen, setPlayerViewOpen] = useState(false);

  // Load mappings when level or category changes
  useEffect(() => {
    loadMappings();
    loadMasterItems();
  }, [sport, selectedLevel, selectedCategory]);

  const loadMappings = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/admin/level-mappings?sport=${sport}&level=${selectedLevel}&type=${selectedCategory}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        const mappedItems = data.data.map((item: any) => ({
          id: item.id,
          code: item.code || item.masterCode,
          name: item.name || item.code,
          isRequired: item.isRequired || false,
          isOptional: item.isOptional !== false,
          displayOrder: item.displayOrder || 0,
        }));
        setMappings(mappedItems);
        setSelectedCodes(new Set(mappedItems.map((m: LevelMapping) => m.code)));
      } else {
        setError(data.message || 'Failed to load mappings');
      }
    } catch (err: any) {
      setError('Failed to load mappings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMasterItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/admin/level-mappings/master-items?sport=${sport}&type=${selectedCategory}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        setMasterItems(data.data);
      }
    } catch (err: any) {
      console.error('Failed to load master items:', err);
    }
  };

  const handleToggleItem = (code: string) => {
    const newSelected = new Set(selectedCodes);
    if (newSelected.has(code)) {
      newSelected.delete(code);
    } else {
      newSelected.add(code);
    }
    setSelectedCodes(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedCodes.size === masterItems.length) {
      setSelectedCodes(new Set());
    } else {
      setSelectedCodes(new Set(masterItems.map(item => item.code)));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const mappingsToSave = Array.from(selectedCodes).map((code, index) => {
        const existing = mappings.find(m => m.code === code);
        return {
          code,
          isRequired: existing?.isRequired || false,
          isOptional: existing?.isOptional !== false,
          displayOrder: existing?.displayOrder || index,
        };
      });

      const response = await fetch('/api/admin/level-mappings/bulk', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sport,
          level: selectedLevel,
          type: selectedCategory,
          mappings: mappingsToSave,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(`Successfully saved ${mappingsToSave.length} ${selectedCategory} for ${skillLevels.find(l => l.value === selectedLevel)?.label}`);
        loadMappings();
      } else {
        setError(data.message || 'Failed to save mappings');
      }
    } catch (err: any) {
      setError('Failed to save mappings: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Level Mappings: {sport}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Level</InputLabel>
            <Select
              value={selectedLevel}
              label="Level"
              onChange={(e) => setSelectedLevel(e.target.value)}
            >
              {skillLevels.map(level => (
                <MenuItem key={level.value} value={level.value}>
                  {level.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              label="Category"
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map(cat => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadMappings}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || loading}
          >
            Save Changes
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Select {categories.find(c => c.value === selectedCategory)?.label} for {skillLevels.find(l => l.value === selectedLevel)?.label}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              size="small"
              onClick={handleSelectAll}
            >
              {selectedCodes.size === masterItems.length ? 'Deselect All' : 'Select All'}
            </Button>
            <Chip
              label={`${selectedCodes.size} of ${masterItems.length} selected`}
              color="primary"
              variant="outlined"
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ position: 'relative' }}>
            <TableContainer 
              ref={tableContainerRef}
              component={Paper} 
              sx={{ 
                maxHeight: 600,
                border: '4px solid #2196f3',
                borderRadius: '4px',
                overflow: 'auto',
              }}
            >
              <Table stickyHeader sx={{ borderCollapse: 'separate', minWidth: 1400 }}>
                <TableHead>
                  <TableRow 
                    sx={{ 
                      background: '#f1f8e9',
                      backgroundImage: `
                        repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(76, 175, 80, 0.2) 3px, rgba(76, 175, 80, 0.2) 4px)
                      `,
                      borderBottom: '4px solid #2196f3',
                      '& .MuiTableCell-root': {
                        borderBottom: '4px solid #2196f3',
                        borderRight: '1px solid rgba(33, 150, 243, 0.7)',
                        '&:last-child': {
                          borderRight: 'none',
                        },
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        backgroundColor: '#f1f8e9',
                      },
                    }}
                  >
                  <TableCell padding="checkbox" sx={{ width: 50, fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'center' }}>
                    <Checkbox
                      checked={selectedCodes.size === masterItems.length && masterItems.length > 0}
                      indeterminate={selectedCodes.size > 0 && selectedCodes.size < masterItems.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>Code</TableCell>
                  {selectedCategory === 'strokes' && (
                    <>
                      <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>Court Position</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left', width: '15%', minWidth: '150px' }}>Half Side</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>Stroke Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>Stroke Action</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>Play Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>Shot Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>Placement</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>Deception</TableCell>
                    </>
                  )}
                  <TableCell sx={{ fontWeight: 700, color: '#1b5e20', fontSize: '0.95rem', py: 1.5, bgcolor: '#f1f8e9', verticalAlign: 'middle', textAlign: 'left' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {masterItems.map((item) => {
                  const isSelected = selectedCodes.has(item.code);
                  const isMapped = mappings.some(m => m.code === item.code);
                  
                  // Get court position style (same as master strokes)
                  const getCourtPositionStyle = (position: string) => {
                    if (!position) return { style: {}, colorBox: null };
                    const pos = position.toLowerCase().trim();
                    if (pos.includes('backcourt') || pos.includes('back court')) {
                      return {
                        style: { position: 'relative' as const, padding: '8px' },
                        colorBox: {
                          position: 'absolute' as const,
                          bottom: '4px',
                          left: '4px',
                          right: '4px',
                          height: 'calc(25% - 4px)',
                          backgroundColor: '#66bb6a',
                          borderRadius: '2px',
                          zIndex: 0,
                          pointerEvents: 'none' as const,
                          display: 'block',
                        },
                      };
                    }
                    if (pos.includes('midcourt') || pos.includes('mid court')) {
                      return {
                        style: { position: 'relative' as const, padding: '8px' },
                        colorBox: {
                          position: 'absolute' as const,
                          top: '25%',
                          bottom: '25%',
                          left: '4px',
                          right: '4px',
                          backgroundColor: '#66bb6a',
                          borderRadius: '2px',
                          zIndex: 0,
                          pointerEvents: 'none' as const,
                          display: 'block',
                        },
                      };
                    }
                    if (pos.includes('frontcourt') || pos.includes('front court') || pos.startsWith('front')) {
                      return {
                        style: { position: 'relative' as const, padding: '8px', minHeight: '60px' },
                        colorBox: {
                          position: 'absolute' as const,
                          top: '4px',
                          left: '4px',
                          right: '4px',
                          height: 'calc(50% - 4px)',
                          minHeight: '24px',
                          backgroundColor: '#66bb6a',
                          borderRadius: '2px',
                          zIndex: 0,
                          pointerEvents: 'none' as const,
                          display: 'block',
                        },
                      };
                    }
                    return { style: {}, colorBox: null };
                  };

                  // Get half side style (same as master strokes)
                  const getHalfSideStyle = (halfSide: string) => {
                    if (!halfSide) return { style: {}, colorBox: null };
                    const side = halfSide.toLowerCase();
                    if (side.includes('right')) {
                      return {
                        style: { position: 'relative' as const, padding: '8px' },
                        colorBox: {
                          position: 'absolute' as const,
                          right: '4px',
                          top: '4px',
                          bottom: '4px',
                          width: 'calc(50% - 4px)',
                          backgroundColor: '#ff9800',
                          borderRadius: '2px',
                          zIndex: 0,
                          pointerEvents: 'none' as const,
                          display: 'block',
                        },
                      };
                    }
                    if (side.includes('left')) {
                      return {
                        style: { position: 'relative' as const, padding: '8px' },
                        colorBox: {
                          position: 'absolute' as const,
                          left: '4px',
                          top: '4px',
                          bottom: '4px',
                          width: 'calc(50% - 4px)',
                          backgroundColor: '#ff9800',
                          borderRadius: '2px',
                          zIndex: 0,
                          pointerEvents: 'none' as const,
                          display: 'block',
                        },
                      };
                    }
                    return { style: {}, colorBox: null };
                  };

                  const courtPositionData = getCourtPositionStyle(item.courtPosition || '');
                  const halfSideData = getHalfSideStyle(item.halfSide || '');
                  const normalizedHalfSide = item.halfSide ? item.halfSide.replace(/Service Court/gi, '').trim() : '-';
                  
                  return (
                    <TableRow
                      key={item.code}
                      hover
                      selected={isSelected}
                      sx={{
                        bgcolor: isSelected ? 'action.selected' : 'transparent',
                        cursor: 'pointer',
                        borderBottom: '1px solid rgba(33, 150, 243, 0.7)',
                        '&:hover': {
                          backgroundColor: isSelected ? 'action.selected' : 'rgba(33, 150, 243, 0.1)',
                        },
                        '& .MuiTableCell-root': {
                          borderBottom: '1px solid rgba(33, 150, 243, 0.7)',
                          borderRight: '1px solid rgba(33, 150, 243, 0.7)',
                          '&:last-child': { borderRight: 'none' },
                        },
                      }}
                      onClick={() => handleToggleItem(item.code)}
                    >
                      <TableCell padding="checkbox" sx={{ verticalAlign: 'middle' }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggleItem(item.code)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, verticalAlign: 'middle' }}>{item.code}</TableCell>
                      {selectedCategory === 'strokes' && (
                        <>
                          <TableCell sx={{ ...courtPositionData.style, fontWeight: 500, position: 'relative', overflow: 'visible', verticalAlign: 'middle' }}>
                            {courtPositionData.colorBox && <Box component="div" sx={courtPositionData.colorBox} />}
                            <Box component="span" sx={{ position: 'relative', zIndex: 1, display: 'inline-block', width: '100%' }}>
                              {item.courtPosition || '-'}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...halfSideData.style, fontWeight: 500, position: 'relative', verticalAlign: 'middle', width: '15%', minWidth: '150px' }}>
                            {halfSideData.colorBox && <Box component="div" sx={halfSideData.colorBox} />}
                            <Box component="span" sx={{ position: 'relative', zIndex: 1, display: 'inline-block', width: '100%' }}>
                              {normalizedHalfSide}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>{item.strokeType || '-'}</TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>{item.strokeAction || '-'}</TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>{item.playType || '-'}</TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>{item.shotType || '-'}</TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>{item.placement || '-'}</TableCell>
                          <TableCell sx={{ verticalAlign: 'middle' }}>{item.deception || '-'}</TableCell>
                        </>
                      )}
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        {isMapped ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Mapped"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            icon={<CancelIcon />}
                            label="Not Mapped"
                            size="small"
                            color="default"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              zIndex: 1000,
            }}
          >
            <IconButton
              onClick={() => {
                if (tableContainerRef.current) {
                  tableContainerRef.current.scrollBy({ top: -100, behavior: 'smooth' });
                }
              }}
              sx={{
                bgcolor: '#1976d2',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1565c0',
                },
              }}
              size="small"
            >
              <ArrowUpwardIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                if (tableContainerRef.current) {
                  tableContainerRef.current.scrollBy({ top: 100, behavior: 'smooth' });
                }
              }}
              sx={{
                bgcolor: '#1976d2',
                color: 'white',
                '&:hover': {
                  bgcolor: '#1565c0',
                },
              }}
              size="small"
            >
              <ArrowDownwardIcon />
            </IconButton>
          </Box>
          </Box>
        )}
      </Paper>

      {selectedCodes.size > 0 && (
        <Paper sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
          <Typography variant="body2">
            <strong>{selectedCodes.size}</strong> {categories.find(c => c.value === selectedCategory)?.label} selected for {skillLevels.find(l => l.value === selectedLevel)?.label}.
            Click "Save Changes" to update the level mapping.
          </Typography>
        </Paper>
      )}

      {/* Show player view button if mappings exist */}
      {mappings.length > 0 && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => setPlayerViewOpen(true)}
          >
            View Player Experience for {skillLevels.find(l => l.value === selectedLevel)?.label}
          </Button>
        </Box>
      )}

      {/* Player View Dialog */}
      <Dialog
        open={playerViewOpen}
        onClose={() => setPlayerViewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Player View: {skillLevels.find(l => l.value === selectedLevel)?.label} - {categories.find(c => c.value === selectedCategory)?.label}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              This is how players will see the {categories.find(c => c.value === selectedCategory)?.label.toLowerCase()} for {skillLevels.find(l => l.value === selectedLevel)?.label.toLowerCase()} level.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                    {selectedCategory === 'strokes' && (
                      <>
                        <TableCell sx={{ fontWeight: 600 }}>Court Position</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Half Side</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Stroke Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Stroke Action</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Play Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Shot Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Placement</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Deception</TableCell>
                      </>
                    )}
                    <TableCell sx={{ fontWeight: 600 }}>Required</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Optional</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappings
                    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                    .map((mapping) => {
                      const item = masterItems.find(i => i.code === mapping.code);
                      return (
                        <TableRow key={mapping.code}>
                          <TableCell>{mapping.code}</TableCell>
                          {selectedCategory === 'strokes' && item && (
                            <>
                              <TableCell>{item.courtPosition || '-'}</TableCell>
                              <TableCell>{item.halfSide || '-'}</TableCell>
                              <TableCell>{item.strokeType || '-'}</TableCell>
                              <TableCell>{item.strokeAction || '-'}</TableCell>
                              <TableCell>{item.playType || '-'}</TableCell>
                              <TableCell>{item.shotType || '-'}</TableCell>
                              <TableCell>{item.placement || '-'}</TableCell>
                              <TableCell>{item.deception || '-'}</TableCell>
                            </>
                          )}
                          <TableCell>
                            {mapping.isRequired ? (
                              <Chip label="Required" size="small" color="primary" />
                            ) : (
                              <Chip label="No" size="small" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>
                            {mapping.isOptional ? (
                              <Chip label="Optional" size="small" color="secondary" />
                            ) : (
                              <Chip label="No" size="small" variant="outlined" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            {mappings.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No mappings found for this level. Please map some items first.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlayerViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminLevelMappings;

